import { useEffect, useLayoutEffect, useRef } from "react";

import { KAI_ENABLED, useFeatureEnabled } from "@/context/FeatureFlagsContext";
import { useRightRail } from "@/context/RightRailContext";
import { useMobileShellViewport } from "@/hooks/useMobileShellViewport";

/**
 * On desktop `/feed`, dock the right rail once per visit: Kai (when the
 * feature flag is on) plus a collapsed Notes card at the bottom. Mobile does
 * not open Kai here — the phone shell only has Kai on the dedicated Kai page.
 *
 * Does not re-open after the user manually closes Kai until they leave the page.
 * Notes is collapsed on each visit so the card is present but not taking height.
 */
export function useFeedPageKaiRail(active: boolean) {
  const kaiEnabled = useFeatureEnabled(KAI_ENABLED);
  const isMobile = useMobileShellViewport();
  const { kaiPanelActive, toggleKaiPanel, collapseNotesSection } = useRightRail();
  const openedForVisitRef = useRef(false);
  const collapsedNotesForVisitRef = useRef(false);

  useLayoutEffect(() => {
    if (!active) {
      collapsedNotesForVisitRef.current = false;
      return;
    }
    if (collapsedNotesForVisitRef.current) return;
    collapsedNotesForVisitRef.current = true;
    collapseNotesSection();
  }, [active, collapseNotesSection]);

  useEffect(() => {
    if (!active) {
      openedForVisitRef.current = false;
      return;
    }
    if (isMobile || !kaiEnabled || openedForVisitRef.current) return;

    openedForVisitRef.current = true;
    if (kaiPanelActive) return;

    toggleKaiPanel();
  }, [active, isMobile, kaiEnabled, kaiPanelActive, toggleKaiPanel]);
}
